import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { Loader2, Plus, Upload, Search, Trash2, UserMinus, Users, List as ListIcon, Pencil, FileSpreadsheet } from 'lucide-react';
import { Modal, Badge, btnPrimary, btnGhost, inputCls } from '../ChatAssistant/ui';
import { mailApi, fmtDate, fmtNum } from './api';

const STATUS = {
  subscribed: { tone: 'green', label: 'Subscribed' },
  unsubscribed: { tone: 'gray', label: 'Unsubscribed' },
  bounced: { tone: 'red', label: 'Bounced' },
  complained: { tone: 'red', label: 'Complained' },
};

// Başlık eşleme: Türkçe/İngilizce yaygın adlar
const HEADER_MAP = [
  ['email', /^(e-?mail|e-?posta|eposta|mail|email address|e-posta adresi)$/i],
  ['first_name', /^(first[ _]?name|firstname|ad|adı|isim|name|given name)$/i],
  ['last_name', /^(last[ _]?name|lastname|soyad|soyadı|surname|family name)$/i],
];
function mapHeader(h) {
  const t = String(h || '').trim();
  for (const [key, re] of HEADER_MAP) if (re.test(t)) return key;
  return t;
}

const PAGE = 50;

function ImportModal({ brandId, lists, onClose, onDone }) {
  const [rows, setRows] = useState(null);
  const [fileName, setFileName] = useState('');
  const [listMode, setListMode] = useState(lists.length ? 'existing' : 'new');
  const [listId, setListId] = useState(lists[0]?.id || '');
  const [newListName, setNewListName] = useState('');
  const [consentSource, setConsentSource] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const readFile = async (file) => {
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array', raw: false });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
      const mapped = raw.map((r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [mapHeader(k), String(v).trim()])));
      if (!mapped.length || !('email' in mapped[0])) {
        toast.error('No email column found. Name the column "email" (or "e-posta").');
        return;
      }
      setRows(mapped);
      setFileName(file.name);
      if (!newListName) setNewListName(file.name.replace(/\.[^.]+$/, ''));
    } catch (e) { toast.error(`Could not read file: ${e.message}`); }
  };

  const stats = useMemo(() => {
    if (!rows) return null;
    const cols = Object.keys(rows[0] || {});
    const validRe = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;
    return { cols, valid: rows.filter((r) => validRe.test(r.email)).length };
  }, [rows]);

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        rows, consentSource, consentConfirmed: confirmed,
        ...(listMode === 'existing' && listId ? { listIds: [listId] } : {}),
        ...(listMode === 'new' && newListName.trim() ? { newListName: newListName.trim() } : {}),
      };
      const r = await mailApi(brandId, '/contacts/import', { method: 'POST', body });
      setResult(r);
      onDone();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  if (result) {
    return (
      <Modal title="Import finished" onClose={onClose} footer={<button className={btnPrimary} onClick={onClose}>Done</button>}>
        <dl className="grid grid-cols-2 gap-y-1.5 text-sm">
          <dt className="text-gray-500">New contacts</dt><dd className="font-medium text-gray-900">{fmtNum(result.inserted)}</dd>
          <dt className="text-gray-500">Already existed (updated)</dt><dd className="font-medium text-gray-900">{fmtNum(result.updated)}</dd>
          <dt className="text-gray-500">Invalid email</dt><dd className="font-medium text-gray-900">{fmtNum(result.invalid)}</dd>
          <dt className="text-gray-500">Duplicate in file</dt><dd className="font-medium text-gray-900">{fmtNum(result.duplicate)}</dd>
        </dl>
        {result.notResubscribed > 0 && (
          <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-3">
            {fmtNum(result.notResubscribed)} contacts had unsubscribed, bounced or complained before. They were added to the list but stay excluded from sends.
          </p>
        )}
      </Modal>
    );
  }

  const canSubmit = rows && stats.valid > 0 && consentSource.trim() && confirmed && (listMode === 'existing' ? listId : newListName.trim());

  return (
    <Modal
      wide
      title="Import contacts"
      onClose={() => !busy && onClose()}
      footer={<>
        <button className={btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
        <button className={btnPrimary} onClick={submit} disabled={busy || !canSubmit}>{busy && <Loader2 className="animate-spin" />}Import {stats ? fmtNum(stats.valid) : ''} contacts</button>
      </>}
    >
      <div className="space-y-4">
        <label className="flex flex-col items-center justify-center gap-1.5 border border-dashed border-gray-300 rounded-lg py-6 cursor-pointer hover:bg-gray-50">
          <FileSpreadsheet className="w-5 h-5 text-gray-400" />
          <span className="text-sm text-gray-700">{fileName || 'Choose a CSV or Excel file'}</span>
          <span className="text-[11px] text-gray-500">Needs an <code>email</code> column. <code>first_name</code>, <code>last_name</code> (or ad / soyad) and any other columns are kept as merge fields.</span>
          <input type="file" accept=".csv,.txt,.xlsx,.xls" className="hidden" onChange={(e) => readFile(e.target.files?.[0])} />
        </label>

        {rows && (
          <div className="text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
            <b>{fmtNum(rows.length)}</b> rows · <b>{fmtNum(stats.valid)}</b> valid emails · columns: {stats.cols.map((c) => <code key={c} className="mr-1">{c}</code>)}
          </div>
        )}

        <div>
          <div className="text-xs font-medium text-gray-700 mb-1">Add to list</div>
          <div className="flex gap-3 text-xs mb-2">
            {lists.length > 0 && <label className="flex items-center gap-1.5"><input type="radio" checked={listMode === 'existing'} onChange={() => setListMode('existing')} />Existing list</label>}
            <label className="flex items-center gap-1.5"><input type="radio" checked={listMode === 'new'} onChange={() => setListMode('new')} />New list</label>
          </div>
          {listMode === 'existing' ? (
            <select className={inputCls} value={listId} onChange={(e) => setListId(e.target.value)}>
              {lists.map((l) => <option key={l.id} value={l.id}>{l.name} ({fmtNum(l.total)})</option>)}
            </select>
          ) : (
            <input className={inputCls} placeholder="e.g. Newsletter subscribers" value={newListName} onChange={(e) => setNewListName(e.target.value)} />
          )}
        </div>

        <div>
          <div className="text-xs font-medium text-gray-700 mb-1">Where was consent collected?</div>
          <input className={inputCls} placeholder="e.g. Website signup form, checkout opt-in" value={consentSource} onChange={(e) => setConsentSource(e.target.value)} />
        </div>

        <label className="flex items-start gap-2 text-xs text-gray-700">
          <input type="checkbox" className="mt-0.5" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          <span>These people gave explicit consent to receive marketing emails from this brand, and for Turkish recipients the consent is registered in İYS. Purchased or scraped lists are not allowed.</span>
        </label>
      </div>
    </Modal>
  );
}

function AddContactModal({ brandId, lists, onClose, onDone }) {
  const [f, setF] = useState({ email: '', firstName: '', lastName: '', consentSource: '', listIds: lists[0] ? [lists[0].id] : [] });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await mailApi(brandId, '/contacts', { method: 'POST', body: f }); toast.success('Contact added'); onDone(); onClose(); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const toggle = (id) => setF((x) => ({ ...x, listIds: x.listIds.includes(id) ? x.listIds.filter((i) => i !== id) : [...x.listIds, id] }));
  return (
    <Modal title="Add contact" onClose={() => !busy && onClose()} footer={<>
      <button className={btnGhost} onClick={onClose} disabled={busy}>Cancel</button>
      <button className={btnPrimary} onClick={save} disabled={busy || !f.email.trim() || !f.consentSource.trim()}>{busy && <Loader2 className="animate-spin" />}Add</button>
    </>}>
      <div className="space-y-3">
        <input className={inputCls} placeholder="Email" value={f.email} autoFocus onChange={(e) => setF({ ...f, email: e.target.value })} />
        <div className="grid grid-cols-2 gap-2">
          <input className={inputCls} placeholder="First name" value={f.firstName} onChange={(e) => setF({ ...f, firstName: e.target.value })} />
          <input className={inputCls} placeholder="Last name" value={f.lastName} onChange={(e) => setF({ ...f, lastName: e.target.value })} />
        </div>
        <input className={inputCls} placeholder="Consent source (e.g. in-store form)" value={f.consentSource} onChange={(e) => setF({ ...f, consentSource: e.target.value })} />
        {lists.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {lists.map((l) => (
              <label key={l.id} className="flex items-center gap-1.5 text-xs text-gray-700"><input type="checkbox" checked={f.listIds.includes(l.id)} onChange={() => toggle(l.id)} />{l.name}</label>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default function ContactsTab({ brandId }) {
  const [lists, setLists] = useState([]);
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [listId, setListId] = useState('');
  const [offset, setOffset] = useState(0);
  const [modal, setModal] = useState(null);
  const [newList, setNewList] = useState(null);

  const loadLists = useCallback(async () => {
    try { setLists((await mailApi(brandId, '/lists')).lists); } catch (e) { toast.error(e.message); }
  }, [brandId]);

  const load = useCallback(async () => {
    try {
      const query2 = { limit: PAGE, offset, ...(query ? { q: query } : {}), ...(status ? { status } : {}), ...(listId ? { listId } : {}) };
      setData(await mailApi(brandId, '/contacts', { query: query2 }));
    } catch (e) { toast.error(e.message); setData({ contacts: [], total: 0, counts: {} }); }
  }, [brandId, query, status, listId, offset]);

  useEffect(() => { loadLists(); }, [loadLists]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setTimeout(() => { setOffset(0); setQuery(q.trim()); }, 300); return () => clearTimeout(t); }, [q]);

  const refresh = () => { load(); loadLists(); };
  const listName = (id) => lists.find((l) => Number(l.id) === Number(id))?.name;

  const unsubscribe = async (c) => {
    try { await mailApi(brandId, `/contacts/${c.id}`, { method: 'PATCH', body: { status: 'unsubscribed' } }); refresh(); }
    catch (e) { toast.error(e.message); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.email}? Their send history stays, but the contact is removed.`)) return;
    try { await mailApi(brandId, `/contacts/${c.id}`, { method: 'DELETE' }); refresh(); }
    catch (e) { toast.error(e.message); }
  };
  const saveList = async () => {
    try {
      if (newList.id) await mailApi(brandId, `/lists/${newList.id}`, { method: 'PATCH', body: { name: newList.name } });
      else await mailApi(brandId, '/lists', { method: 'POST', body: { name: newList.name } });
      setNewList(null); loadLists();
    } catch (e) { toast.error(e.message); }
  };
  const deleteList = async (l) => {
    if (!window.confirm(`Delete list "${l.name}"? Contacts are kept.`)) return;
    try { await mailApi(brandId, `/lists/${l.id}`, { method: 'DELETE' }); if (String(listId) === String(l.id)) setListId(''); refresh(); }
    catch (e) { toast.error(e.message); }
  };

  const counts = data?.counts || {};
  const allCount = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold text-gray-900">Contacts</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {fmtNum(allCount)} contacts · {fmtNum(counts.subscribed)} can receive email
            {counts.unsubscribed ? ` · ${fmtNum(counts.unsubscribed)} unsubscribed` : ''}
            {(counts.bounced || counts.complained) ? ` · ${fmtNum((counts.bounced || 0) + (counts.complained || 0))} bounced/complained` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <button className={btnGhost} onClick={() => setModal('add')}><Plus />Add contact</button>
          <button className={btnPrimary} onClick={() => setModal('import')}><Upload />Import</button>
        </div>
      </div>

      <section className="bg-white rounded-xl border border-gray-200">
        <header className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
          <h3 className="text-xs font-semibold text-gray-700 flex items-center gap-1.5"><ListIcon className="w-3.5 h-3.5 text-gray-400" />Lists</h3>
          <button className="text-xs text-indigo-600 hover:text-indigo-800" onClick={() => setNewList({ name: '' })}>+ New list</button>
        </header>
        {lists.length === 0 ? (
          <p className="px-4 py-3 text-xs text-gray-500">No lists yet. Campaigns are sent to lists, so import contacts into one.</p>
        ) : (
          <div className="flex flex-wrap gap-2 p-3">
            {lists.map((l) => {
              const active = String(listId) === String(l.id);
              return (
                <div key={l.id} className={`group flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs cursor-pointer ${active ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'}`}
                  onClick={() => { setOffset(0); setListId(active ? '' : l.id); }}>
                  <span className="font-medium text-gray-800">{l.name}</span>
                  <span className="text-gray-500">{fmtNum(l.subscribed)}<span className="text-gray-400">/{fmtNum(l.total)}</span></span>
                  <button className="hidden group-hover:inline text-gray-400 hover:text-gray-700" onClick={(e) => { e.stopPropagation(); setNewList({ id: l.id, name: l.name }); }}><Pencil className="w-3 h-3" /></button>
                  <button className="hidden group-hover:inline text-gray-400 hover:text-red-600" onClick={(e) => { e.stopPropagation(); deleteList(l); }}><Trash2 className="w-3 h-3" /></button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="bg-white rounded-xl border border-gray-200">
        <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-gray-100">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input className={`${inputCls} pl-8`} placeholder="Search email or name" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <select className={`${inputCls.replace('w-full ', '')} w-44`} value={status} onChange={(e) => { setOffset(0); setStatus(e.target.value); }}>
            <option value="">All statuses</option>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          {listId && <Badge tone="indigo">List: {listName(listId)}</Badge>}
        </div>
        {data === null ? (
          <div className="flex items-center justify-center py-16 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading…</div>
        ) : data.contacts.length === 0 ? (
          <div className="py-14 text-center">
            <Users className="w-6 h-6 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-700 font-medium">{allCount ? 'No contacts match' : 'No contacts yet'}</p>
            {!allCount && <p className="text-xs text-gray-500 mt-1">Import a CSV or Excel file with people who opted in.</p>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
                  <th className="px-4 py-2 font-semibold">Email</th>
                  <th className="px-2 py-2 font-semibold">Name</th>
                  <th className="px-2 py-2 font-semibold">Status</th>
                  <th className="px-2 py-2 font-semibold">Lists</th>
                  <th className="px-2 py-2 font-semibold">Consent</th>
                  <th className="px-2 py-2 font-semibold">Added</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {data.contacts.map((c) => (
                  <tr key={c.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60">
                    <td className="px-4 py-2 text-gray-900">{c.email}</td>
                    <td className="px-2 py-2 text-gray-700">{[c.firstName, c.lastName].filter(Boolean).join(' ') || <span className="text-gray-300">—</span>}</td>
                    <td className="px-2 py-2"><Badge tone={STATUS[c.status]?.tone}>{STATUS[c.status]?.label || c.status}</Badge></td>
                    <td className="px-2 py-2 text-gray-600">{c.listIds.map(listName).filter(Boolean).join(', ') || <span className="text-gray-300">—</span>}</td>
                    <td className="px-2 py-2 text-gray-500 max-w-[180px] truncate" title={c.consentSource}>{c.consentSource || '—'}</td>
                    <td className="px-2 py-2 text-gray-500 whitespace-nowrap">{fmtDate(c.createdAt)}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {c.status === 'subscribed' && <button className="text-gray-400 hover:text-gray-700 mr-2" title="Unsubscribe" onClick={() => unsubscribe(c)}><UserMinus className="w-3.5 h-3.5" /></button>}
                      <button className="text-gray-400 hover:text-red-600" title="Delete" onClick={() => remove(c)}><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && data.total > PAGE && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 text-xs text-gray-500">
            <span>{fmtNum(offset + 1)}–{fmtNum(Math.min(offset + PAGE, data.total))} of {fmtNum(data.total)}</span>
            <div className="flex gap-2">
              <button className={btnGhost} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</button>
              <button className={btnGhost} disabled={offset + PAGE >= data.total} onClick={() => setOffset(offset + PAGE)}>Next</button>
            </div>
          </div>
        )}
      </section>

      {modal === 'import' && <ImportModal brandId={brandId} lists={lists} onClose={() => setModal(null)} onDone={refresh} />}
      {modal === 'add' && <AddContactModal brandId={brandId} lists={lists} onClose={() => setModal(null)} onDone={refresh} />}
      {newList && (
        <Modal title={newList.id ? 'Rename list' : 'New list'} onClose={() => setNewList(null)} footer={<>
          <button className={btnGhost} onClick={() => setNewList(null)}>Cancel</button>
          <button className={btnPrimary} onClick={saveList} disabled={!newList.name.trim()}>Save</button>
        </>}>
          <input className={inputCls} autoFocus value={newList.name} placeholder="List name" onChange={(e) => setNewList({ ...newList, name: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter' && newList.name.trim()) saveList(); }} />
        </Modal>
      )}
    </div>
  );
}
